/**
 * BusinessApiClient -- HTTP client for /api/businesses.
 *
 * @module infrastructure/api/BusinessApiClient
 */

import { ApiBase } from './ApiBase.js';

const BASE = '/api/businesses';

export const BusinessApiClient = {
  /** Los negocios que trabajan con Nexo (con NEGOCIOS-ARCHIVAR-API-T1 la API ya no trae los archivados sin ?status). */
  async list() {
    return ApiBase.get(`${BASE}/all`);
  },

  /** Todos, archivados incluidos (?status=all; la API de antes ignora el parametro y tambien los trae todos). */
  async listTodos() {
    return ApiBase.get(`${BASE}/all?status=all`);
  },

  /** NEGOCIOS-ARCHIVAR-API-T1: archiva con motivo; 409 ARCHIVAR_SUSCRIPCION_VIGENTE pide confirmarSuscripcionVigente. */
  async archivar(id, cuerpo) {
    return ApiBase.post(`${BASE}/${encodeURIComponent(id)}/archivar`, cuerpo);
  },

  async desarchivar(id) {
    return ApiBase.post(`${BASE}/${encodeURIComponent(id)}/desarchivar`, {});
  },

  /**
   * PCs por negocio de v_negocios_equipos (LIC-UN-PC-POR-NEGOCIO-T1), con la excepcion declarada. Ruta de A (#22549):
   * {success, data:[{businessId, negocio, pcsActivos, filasActivas, pcsPermitidos, excede, ultimoLastSeen}]}; llega
   * con el PR de EQUIPOS. Mientras responde 404 y la vista cuenta los PCs con /api/license/machines.
   */
  async equipos() {
    return ApiBase.get('/api/v1/equipos/por-negocio');
  },

  async getById(id) {
    return ApiBase.get(`${BASE}/${id}`);
  },

  /**
   * FND-T1: la descripcion de marca (contrato de A #23156). GET responde 200 con completa:false y nulls si no hay marca;
   * 404 = FND-T2 sin publicar. Las reglas viven en application/negocios/marcaDelNegocio.js.
   */
  async getMarca(id) {
    return ApiBase.get(`${BASE}/${encodeURIComponent(id)}/marca`);
  },

  /** FND-T1: guarda la marca. El cuerpo lo arma cuerpoDeMarca (solo camelCase). */
  async guardarMarca(id, cuerpo) {
    return ApiBase.put(`${BASE}/${encodeURIComponent(id)}/marca`, cuerpo);
  },

  async create(business) {
    return ApiBase.post(BASE, business);
  },

  async update(id, business) {
    return ApiBase.put(`${BASE}/${id}`, business);
  },

  async remove(id) {
    return ApiBase.del(`${BASE}/${id}`);
  },

  async getUsers(id) {
    return ApiBase.get(`${BASE}/${id}/users`);
  },

  async addUser(businessId, userId) {
    return ApiBase.post(`${BASE}/${businessId}/users`, { userId });
  },

  async removeUser(businessId, userId) {
    return ApiBase.del(`${BASE}/${businessId}/users`, { userId });
  },

  async getConfig() {
    return ApiBase.get(`${BASE}/config`);
  },

  /** Actualiza logo_url, fondo_url y/o colores en account_config. Solo envía las claves presentes. */
  async updateAccountConfig(id, config) {
    return ApiBase.patch(`${BASE}/${id}/account-config`, config);
  },

  /**
   * PANEL-OPS-V2-001 T3 — Config operativa por negocio.
   *
   * Endpoint: /api/v1/businesses/{id}/ops-config
   *   - GET    → devuelve { asistencia_strict_mode, umbral_tardanza_minutos, periodo_gracia_dias }
   *   - PATCH  → actualiza solo las claves enviadas
   *
   * Mergeado en nexo-api PR #1 (commit 7f0e4a4).
   */
  async getOpsConfig(id) {
    return ApiBase.get(`/api/v1/businesses/${id}/ops-config`);
  },

  async updateOpsConfig(id, config) {
    return ApiBase.patch(`/api/v1/businesses/${id}/ops-config`, config);
  },

  /**
   * Lista los usuarios panel_auth asociados a un negocio (admin Nexo only).
   * Retorna { businessId, count, users: [{email, displayName, role, createdAt, updatedAt}] }.
   */
  async getPanelUsers(id) {
    return ApiBase.get(`/api/auth/business/${id}/credentials`);
  },

  /**
   * Regenera la contrasena de UN usuario especifico del negocio (admin Nexo only).
   * Retorna { email, password (plano, una sola vez), mensaje }.
   */
  async resetPanelPassword(id, email) {
    return ApiBase.post(`/api/auth/business/${id}/reset-password`, { email });
  },

  /**
   * Crea un usuario panel_auth para el negocio (admin Nexo only).
   * body: { email?, displayName?, role? } - todos opcionales, defaults en el backend.
   * Retorna { email, password (plano, una sola vez), displayName, role, mensaje }.
   */
  async createPanelUser(id, body = {}) {
    return ApiBase.post(`/api/auth/business/${id}/create-user`, body);
  },

  /**
   * ARGEMIRO-ONBOARDING-001 / ONB-T7 -- crea la invitacion de onboarding para
   * un negocio ya existente y devuelve el token no-adivinable. El link publico
   * se arma con #/onboard/{token} (NO #/onboarding/{bizId}: esa ruta no existe
   * y expondria el business_id). Requiere JWT del panel (endpoint [Authorize]).
   * Retorna { token, businessId, status, createdAt }.
   */
  async createOnboardingInvitation(businessId) {
    return ApiBase.post('/api/v1/onboarding/invitations', { businessId });
  }
};
